/**
 * Read-aloud — typed input, spoken replies. Client-side TTS of assistant
 * responses via the browser's `speechSynthesis`; no backend, no provider
 * credential, works with every agent regardless of `enableVoice`. This is
 * deliberately distinct from realtime voice sessions (Grok Voice over the
 * direct socket) — it's the lightweight "I'll type, you talk" mode.
 *
 * The enabled flag is a module store (`useSyncExternalStore` idiom, persisted
 * to localStorage) so the composer toggle and the per-message speak effect
 * stay in sync without context plumbing.
 */

import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'voice-read-aloud-enabled';

function readStoredEnabled(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

class ReadAloudStore {
  private enabled = readStoredEnabled();
  private listeners = new Set<() => void>();

  getSnapshot = (): boolean => this.enabled;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    try {
      localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    } catch {
      /* private mode etc. — session-only is fine */
    }
    if (!enabled) stopSpeaking();
    for (const l of this.listeners) l();
  }
}

export const readAloudStore = new ReadAloudStore();

export function useReadAloudEnabled(): boolean {
  return useSyncExternalStore(readAloudStore.subscribe, readAloudStore.getSnapshot);
}

/**
 * Reduce markdown to something a TTS voice can read without reciting syntax.
 * Heuristic, not a parser: code blocks are announced and skipped, link/image
 * syntax collapses to its label, list/heading/table chrome is stripped.
 */
export function stripForSpeech(markdown: string): string {
  let s = markdown;
  s = s.replace(/```[\s\S]*?```/g, ' Code block omitted. ');
  s = s.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1');
  s = s.replace(/\[([^\]]*)\]\([^)]*\)/g, '$1');
  s = s.replace(/`([^`]*)`/g, '$1');
  s = s.replace(/^#{1,6}\s+/gm, '');
  s = s.replace(/^\s*>\s?/gm, '');
  s = s.replace(/^\s*[-*+]\s+/gm, '');
  s = s.replace(/^\s*\d+\.\s+/gm, '');
  // Table separator rows (|---|---|) vanish; remaining pipes become pauses.
  s = s.replace(/^\s*\|?[\s:|-]+\|\s*$/gm, '');
  s = s.replace(/\|/g, ', ');
  s = s.replace(/(\*\*\*|\*\*|\*|___|__|_|~~)(.*?)\1/g, '$2');
  s = s.replace(/<[^>]+>/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s;
}

function synth(): SpeechSynthesis | null {
  if (typeof window === 'undefined') return null;
  return 'speechSynthesis' in window ? window.speechSynthesis : null;
}

/** Speak `text` (markdown ok — it gets stripped). Cancels anything queued. */
export function speakText(text: string): void {
  const s = synth();
  if (!s) return;
  const cleaned = stripForSpeech(text);
  if (!cleaned) return;
  s.cancel();
  const utterance = new SpeechSynthesisUtterance(cleaned);
  s.speak(utterance);
}

export function stopSpeaking(): void {
  synth()?.cancel();
}
